import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { resolveAccessorialsStatus, updateAccessorialException, } from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { buildUpdatePayload, buildResolutionPayload, } from '../accessorials/accessorialRequestPaneUtils';
export default function useAccessorialMutations({ accessorial, accessorialId, formFields, fullShipment, onSave, refresh, }) {
    // Edit mutation
    const editMutation = useMutation({
        mutationFn: (updates) => updateAccessorialException(updates),
        onSuccess: (res) => {
            var _a;
            if ((_a = res.entity) === null || _a === void 0 ? void 0 : _a.success) {
                refresh === null || refresh === void 0 ? void 0 : refresh();
                onSave === null || onSave === void 0 ? void 0 : onSave();
            }
            else {
                showToast('Update failed', 'ACCESSORIAL_UPDATE_FAIL', 'error');
            }
        },
        onError: (e) => {
            console.error('Failed to update accessorial:', e);
            showToast(e.message || 'Update failed', 'ACCESSORIAL_UPDATE_FAIL', 'error');
        },
    });
    // Approve mutation
    const approveMutation = useMutation({
        mutationFn: (payload) => resolveAccessorialsStatus(payload),
        onSuccess: () => {
            refresh === null || refresh === void 0 ? void 0 : refresh();
            onSave === null || onSave === void 0 ? void 0 : onSave();
        },
        onError: (e) => {
            console.error('Failed to approve accessorial:', e);
            showToast(e.message || 'Approve failed', 'ACCESSORIAL_APPROVE_FAIL', 'error');
        },
    });
    // Decline mutation
    const declineMutation = useMutation({
        mutationFn: (payload) => resolveAccessorialsStatus(payload),
        onSuccess: () => {
            refresh === null || refresh === void 0 ? void 0 : refresh();
            onSave === null || onSave === void 0 ? void 0 : onSave();
        },
        onError: (e) => {
            console.error('Failed to decline accessorial:', e);
            showToast(e.message || 'Decline failed', 'ACCESSORIAL_DECLINE_FAIL', 'error');
        },
    });
    // Extract stable .mutate refs for useCallback deps
    const { mutate: editMutate } = editMutation;
    const { mutate: approveMutate } = approveMutation;
    const { mutate: declineMutate } = declineMutation;
    const handleSaveButton = useCallback((type) => {
        const updated = buildUpdatePayload(formFields, accessorial, type);
        if (updated) {
            editMutate([updated]);
        }
    }, [formFields, accessorial, editMutate]);
    const handleRequestAction = useCallback((type, action, declineNote, declineReasonCode) => {
        const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
            isVendorChargePaid(accessorialId, fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.vendor_payables);
        const { payload, validationError } = buildResolutionPayload(accessorial, {
            action,
            type,
            declineNote,
            declineReasonCode,
            isVendorPaid,
            isBulk: false,
            shipmentId: fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment._id,
        });
        if (validationError)
            return;
        if (action === 'approve') {
            approveMutate(payload);
        }
        else {
            declineMutate(payload);
        }
    }, [accessorial, accessorialId, fullShipment, approveMutate, declineMutate]);
    const handleConfirmBulk = useCallback((bulkEvent, declineNote, declineReasonCode) => {
        const action = bulkEvent === 'approve-all' ? 'approve' : 'decline';
        const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
            isVendorChargePaid(accessorialId, fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.vendor_payables);
        const { payload, validationError } = buildResolutionPayload(accessorial, {
            action,
            declineNote,
            declineReasonCode,
            isVendorPaid,
            isBulk: true,
            shipmentId: fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment._id,
        });
        if (validationError)
            return;
        if (action === 'approve') {
            approveMutate(payload);
        }
        else {
            declineMutate(payload);
        }
    }, [accessorial, accessorialId, fullShipment, approveMutate, declineMutate]);
    const handleEquipmentSelect = useCallback((selected, equipmentId) => {
        if (selected)
            return;
        editMutate([{ _id: formFields._id, equipment: [equipmentId] }]);
    }, [formFields._id, editMutate]);
    return {
        isEditSaving: editMutation.isLoading,
        isApproveSaving: approveMutation.isLoading,
        isDeclineSaving: declineMutation.isLoading,
        handleSaveButton,
        handleRequestAction,
        handleConfirmBulk,
        handleEquipmentSelect,
    };
}
